import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Download,
  FileSpreadsheet,
  GraduationCap,
  KeyRound,
  MoreVertical,
  Printer,
  UserCheck,
  UserX,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TEACHING_ROLES } from '@/constants/roles';
import { PrintDocument } from '@/components/ui/PrintDocument';
import { env } from '@/config/env';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { instituteToday } from '@/features/teacher/utils';
import { toast } from '@/hooks/useToast';
import { apiClient, ApiRequestError } from '@/lib/apiClient';
import { downloadCsv } from '@/lib/download';
import { formatClockTime, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { StaffMember } from '@/types/staff';
import type { StaffAttendanceSummary } from '@/types/staffAttendance';
import { staffApi } from '../api/staff.api';
import { staffRecordApi } from '../api/staffLeave.api';
import { formatWorked, timeOf } from '../attendanceFormat';
import { setStaffStatus } from '../staffStatus';
import { ATTENDANCE_DAY_LABEL } from '../constants';
import { announceAllotmentSaved, ClassAllotmentModal } from './ClassAllotmentModal';
import { StaffIdCard, StaffProfileSheet } from './StaffPrintDocuments';

type Busy = 'profile' | 'idCard' | 'export' | 'invite' | 'status' | null;

type PrintTarget =
  | {
      kind: 'profile';
      photoUrl: string | null;
      attendance: StaffAttendanceSummary | null;
      monthLabel: string;
    }
  | { kind: 'idCard'; photoUrl: string | null };

export interface StaffProfileActionsProps {
  member: StaffMember;
  /** `STAFF_MANAGE` — the invite and the status change are writes. */
  canManage: boolean;
  /** Raised with the saved record after the status changes. */
  onChanged: (member: StaffMember) => void;
}

/** `2026-09` and `September 2026` for the month we are in. */
function currentMonth(): { value: string; label: string } {
  // The school's month, not the device's: on the evening of the last day a
  // laptop in another zone would otherwise fetch next month's register.
  const value = instituteToday().slice(0, 7);
  return {
    value,
    label: new Date(`${value}-01T00:00:00.000Z`).toLocaleDateString('en-IN', {
      timeZone: 'UTC',
      month: 'long',
      year: 'numeric',
    }),
  };
}

/**
 * The photograph as an object URL, fetched before printing rather than by a
 * `SecureImage` inside the document: `PrintDocument` prints on the next frame,
 * long before an image requested from there would arrive. The caller revokes it.
 */
async function fetchPhotoUrl(member: StaffMember): Promise<string | null> {
  if (!member.hasPhoto) return null;
  try {
    const response = await apiClient.get<Blob>(staffApi.photoPath(member.uuid), { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    // Decoded now, so the one frame before the print dialog is enough to paint it.
    const image = new Image();
    image.src = url;
    await image.decode().catch(() => undefined);
    return url;
  } catch {
    // Initials stand in — a photograph that will not load must not stop the print.
    return null;
  }
}

function errorMessage(caught: unknown): string {
  return caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.';
}

interface MenuItemProps {
  icon: ReactNode;
  title: string;
  description: string;
  onSelect: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}

function MenuItem({ icon, title, description, onSelect, disabled = false, tone = 'default' }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'w-full px-3.5 py-2 text-left flex items-start gap-2.5 transition-colors cursor-pointer group disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-500',
        tone === 'danger'
          ? 'hover:bg-rose-50 dark:hover:bg-rose-950/40'
          : 'hover:bg-slate-50 dark:hover:bg-slate-800/60',
      )}
    >
      <span className="mt-0.5 shrink-0" aria-hidden>
        {icon}
      </span>
      <span>
        <span
          className={cn(
            'block text-xs font-bold',
            tone === 'danger' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white',
          )}
        >
          {title}
        </span>
        <span
          className={cn(
            'block text-[11px]',
            tone === 'danger' ? 'text-rose-500 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400',
          )}
        >
          {description}
        </span>
      </span>
    </button>
  );
}

/**
 * The profile's "More actions" menu.
 *
 * Every entry is backed by something the API already does — the profile PDF and
 * ID card print through `PrintDocument` ("Save as PDF" is the browser's own), the
 * export is the attendance the Attendance tab reads, and the invite and status
 * change are the same calls the register makes. The owner's account is refused
 * every write by the server, so those entries are not offered for it at all.
 */
export function StaffProfileActions({ member, canManage, onChanged }: StaffProfileActionsProps) {
  const branding = useInstituteBranding((state) => state.branding);
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [printTarget, setPrintTarget] = useState<PrintTarget | null>(null);
  const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const canWrite = canManage && !member.isOwner;
  /** The same editor the staff list's ⋮ opens — it starts from what they already take. */
  const canAllot = canManage && TEACHING_ROLES.includes(member.staffType);
  const [isAllotting, setIsAllotting] = useState(false);
  const isActive = member.status === 'ACTIVE';
  // The paper must never go out headed with another institute's name.
  const instituteName = branding?.displayName ?? env.appName;
  const instituteCity = branding?.city ?? null;

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  function choose(action: () => void) {
    setIsOpen(false);
    action();
  }

  async function printProfile() {
    setBusy('profile');
    const month = currentMonth();
    const [photoUrl, attendance] = await Promise.all([
      fetchPhotoUrl(member),
      staffRecordApi
        .attendanceMonth(member.uuid, month.value)
        .then((data) => data.summary)
        // The rest of the record is still worth printing; the sheet says so.
        .catch(() => null),
    ]);
    setPrintTarget({ kind: 'profile', photoUrl, attendance, monthLabel: month.label });
    setBusy(null);
  }

  async function printIdCard() {
    setBusy('idCard');
    const photoUrl = await fetchPhotoUrl(member);
    setPrintTarget({ kind: 'idCard', photoUrl });
    setBusy(null);
  }

  function finishPrinting() {
    if (printTarget?.photoUrl) URL.revokeObjectURL(printTarget.photoUrl);
    setPrintTarget(null);
  }

  async function exportAttendance() {
    setBusy('export');
    const month = currentMonth();
    try {
      const data = await staffRecordApi.attendanceMonth(member.uuid, month.value);
      const days = data.days.filter((day) => day.kind !== 'UPCOMING');

      if (days.length === 0) {
        toast.info('Nothing to export yet', `No working day of ${month.label} has passed.`);
        return;
      }

      downloadCsv(`Attendance-${member.employeeCode || member.fullName}-${month.value}.csv`, [
        ['Date', 'Day', 'Status', 'In time', 'Out time', 'Hours worked', 'Late', 'Note'],
        ...days.map((day) => [
          formatDate(day.date),
          day.weekday,
          ATTENDANCE_DAY_LABEL[day.kind],
          day.punchedInAt ? formatClockTime(timeOf(day.punchedInAt)) : '',
          day.punchedOutAt ? formatClockTime(timeOf(day.punchedOutAt)) : '',
          day.workedMinutes === null ? '' : formatWorked(day.workedMinutes),
          day.isLate ? 'Yes' : '',
          day.holidayName ?? '',
        ]),
      ]);
    } catch (caught) {
      toast.error('Could not export the attendance', errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  async function sendResetLink() {
    setBusy('invite');
    try {
      await staffApi.resendInvite(member.uuid);
      toast.success(
        'Email sent to the registered address',
        `A set-password link was emailed to ${member.email}. Any earlier link has stopped working.`,
      );
    } catch (caught) {
      toast.error('Could not send that link', errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  async function changeStatus() {
    setBusy('status');
    try {
      const outcome = await setStaffStatus(member, isActive ? 'INACTIVE' : 'ACTIVE');
      setIsConfirmingStatus(false);
      if (!outcome.ok) {
        toast.error(
          'This record needs correcting first',
          'Open Edit profile, fix the highlighted details and save, then change the status.',
        );
        return;
      }

      const { saved } = outcome;
      toast.success(
        isActive ? `${saved.fullName} has been suspended` : `${saved.fullName} has been reactivated`,
        isActive ? 'They have been signed out and cannot sign in until reactivated.' : undefined,
      );
      onChanged(saved);
    } catch (caught) {
      toast.error('Could not change the status', errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-busy={busy !== null}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1 border border-slate-300 dark:border-slate-700 rounded-xl text-xs font-bold transition-all shadow-2xs cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500',
          isOpen
            ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white'
            : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200',
        )}
      >
        <MoreVertical className="w-3.5 h-3.5 text-slate-500" aria-hidden />
        <span>{busy ? 'Working…' : 'More actions'}</span>
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label={`Actions for ${member.fullName}`}
          className="absolute right-0 mt-1.5 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-50 py-1.5 divide-y divide-slate-100 dark:divide-slate-800"
        >
          <div className="py-1">
            <MenuItem
              icon={<Download className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
              title="Download Full Profile (PDF)"
              description="Choose “Save as PDF” in the print dialog"
              disabled={busy !== null}
              onSelect={() => choose(() => void printProfile())}
            />
            <MenuItem
              icon={<Printer className="w-4 h-4 text-sky-600 dark:text-sky-400" />}
              title="Print Staff ID Card"
              description="Standard card size, ready to cut out"
              disabled={busy !== null}
              onSelect={() => choose(() => void printIdCard())}
            />
            {canWrite && (
              <MenuItem
                icon={<KeyRound className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
                title={member.invitePending ? 'Resend Invite' : 'Reset Password & Access'}
                description={
                  isActive
                    ? `Send a set-password link to ${member.email}`
                    : 'Reactivate the account before sending a link'
                }
                disabled={busy !== null || !isActive}
                onSelect={() => choose(() => void sendResetLink())}
              />
            )}
            {canAllot && (
              <MenuItem
                icon={<GraduationCap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                title="Assign or edit classes"
                description="The sections and subjects they teach this year"
                disabled={busy !== null}
                onSelect={() => choose(() => setIsAllotting(true))}
              />
            )}
            <MenuItem
              icon={<FileSpreadsheet className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
              title="Export Attendance Log"
              description="This month’s daily attendance as CSV"
              disabled={busy !== null}
              onSelect={() => choose(() => void exportAttendance())}
            />
          </div>

          {canWrite && (
            <div className="py-1">
              <MenuItem
                icon={
                  isActive ? (
                    <UserX className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  ) : (
                    <UserCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  )
                }
                title={isActive ? 'Suspend Account' : 'Reactivate Account'}
                description={isActive ? 'Sign them out and block sign-in' : 'Let them sign in again'}
                tone={isActive ? 'danger' : 'default'}
                disabled={busy !== null}
                onSelect={() => choose(() => setIsConfirmingStatus(true))}
              />
            </div>
          )}
        </div>
      )}

      {isAllotting && (
        <ClassAllotmentModal
          staff={member}
          isOpen
          onClose={() => setIsAllotting(false)}
          onSaved={(allocations) => {
            announceAllotmentSaved(member.fullName, allocations);
            setIsAllotting(false);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmingStatus}
        title={
          isActive ? (
            <>
              Suspend <span className="inline-block px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold">{member.fullName}</span>?
            </>
          ) : (
            <>
              Reactivate <span className="inline-block px-1.5 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold">{member.fullName}</span>?
            </>
          )
        }
        description={
          isActive
            ? 'They are signed out everywhere at once and cannot sign in until the account is reactivated. Their records stay as they are.'
            : 'They will be able to sign in again with their existing password.'
        }
        confirmLabel={isActive ? 'Suspend' : 'Reactivate'}
        tone={isActive ? 'danger' : 'primary'}
        isLoading={busy === 'status'}
        onConfirm={() => void changeStatus()}
        onCancel={() => setIsConfirmingStatus(false)}
      />

      <PrintDocument
        isOpen={printTarget !== null}
        onFinished={finishPrinting}
        fileName={`${printTarget?.kind === 'idCard' ? 'ID-card' : 'Staff-profile'}-${member.employeeCode || member.fullName}`}
      >
        {printTarget?.kind === 'profile' && (
          <StaffProfileSheet
            member={member}
            instituteName={instituteName}
            instituteCity={instituteCity}
            photoUrl={printTarget.photoUrl}
            attendance={printTarget.attendance}
            attendanceMonthLabel={printTarget.monthLabel}
          />
        )}
        {printTarget?.kind === 'idCard' && (
          <StaffIdCard
            member={member}
            instituteName={instituteName}
            instituteCity={instituteCity}
            photoUrl={printTarget.photoUrl}
          />
        )}
      </PrintDocument>
    </div>
  );
}
